registerComponent("templates/header-redesign", function (component) {
/**
 * HEADER — comportamentos do topo da página.
 *
 * · sombra ao sair do topo;
 * · alternância do menu de gaveta abaixo de 1024px — aberta, ela cobre a tela
 *   (frame 10680:55691, ADR 0037) e trava a rolagem da página;
 * · o menu de idiomas (frame 10680:19019, ADR 0036): um véu com blur sobre a
 *   página e a lista pendurada no botão "BR" — abre e fecha por classe no
 *   header, com teclado (Esc, setas, Home/End) e trava a rolagem enquanto
 *   aberto;
 * · entrada da linha do item ativo (ver Asa.revelar em assets/.scripts/asa.js).
 *
 * O Figma não especifica esses estados: eles existem porque a navegação não cabe
 * em telas estreitas.
 *
 * @param {HTMLElement} component - elemento raiz do componente (o <header class="header">)
 * @param {Object} params
 */

const SCROLLED_CLASS = 'header--scrolled';
const SCROLL_THRESHOLD = 8;

const burger = component.querySelector('.header__burger');
const nav = component.querySelector('.header__nav');

/* Marca o header assim que a página sai do topo. Um quadro por rajada de
   rolagem, pelo agendador de `Asa.aoRolar` — antes a classe era alternada em
   TODO evento de scroll, que é o único dos cinco pontos de rolagem da home que
   não tinha `requestAnimationFrame`. */
Asa.aoRolar((y) => {
    component.classList.toggle(SCROLLED_CLASS, y > SCROLL_THRESHOLD);
});

/* Abre e fecha a navegação em telas estreitas. */
if (burger && nav) {
    /* A gaveta ABRE e FECHA em transição (04/09 à noite, ADR 0038): a classe
       `header--menu-open` muda o `display` e a geometria; `header--menu-in`,
       no quadro seguinte, arma a dissolução e a descida do conteúdo (ver
       _header.less). Fechando (05/09, ADR 0040), `--in` sai e
       `header--menu-fechando` entra: com `--open` ainda no ar a barra
       transiciona de volta a 64, transparente, no mesmo tempo da abertura; só
       depois saem `--open` e `--fechando` — o tempo é `SAIDA_GAVETA`, o da
       transição mais longa da barra (0,32s) mais um quadro. */
    const SAIDA_GAVETA = 340;
    let timerGaveta = 0;

    const setOpen = (open) => {
        burger.setAttribute('aria-expanded', String(open));
        burger.setAttribute('aria-label', open ? 'Fechar menu' : 'Abrir menu');
        clearTimeout(timerGaveta);
        // Abaixo do degrau a gaveta cobre a tela e a página não pode rolar por
        // baixo dela (`html.js-menu-aberto { overflow: hidden }`, _base.less).
        document.documentElement.classList.toggle('js-menu-aberto', open);

        if (open) {
            // Reabrindo no meio do fechamento, a barra volta a crescer de onde
            // estava.
            component.classList.remove('header--menu-fechando');
            component.classList.add('header--menu-open');

            if (Asa.movimentoPermitido()) {
                void component.offsetWidth;
                requestAnimationFrame(() => component.classList.add('header--menu-in'));
            } else {
                component.classList.add('header--menu-in');
            }

            return;
        }

        component.classList.remove('header--menu-in', 'header--idiomas-in');
        component.classList.add('header--menu-fechando');

        // Fechada, a gaveta volta ao modo de navegação (ver o painel de
        // idiomas, abaixo).
        const fim = () => component.classList.remove('header--menu-open', 'header--menu-fechando', 'header--menu-idiomas');

        if (Asa.movimentoPermitido()) {
            timerGaveta = setTimeout(fim, SAIDA_GAVETA);
        } else {
            fim();
        }
    };

    burger.addEventListener('click', () => {
        setOpen(burger.getAttribute('aria-expanded') !== 'true');
        Asa.track('menu_toggle', { target_id: 'main', state: burger.getAttribute('aria-expanded') === 'true' ? 'open' : 'close' });
    });

    // Esc fecha, como em qualquer overlay.
    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && burger.getAttribute('aria-expanded') === 'true') {
            setOpen(false);
            burger.focus();
        }
    });

    // Clicar fora fecha. Sem isso a gaveta cobre o hero e só o próprio botão a
    // dispensa, que não é o que se espera de um overlay.
    document.addEventListener('click', (event) => {
        if (burger.getAttribute('aria-expanded') !== 'true') return;
        if (component.contains(event.target)) return;
        setOpen(false);
    });

    // Navegar por um link da gaveta também a fecha. "Ajuda e Suporte" não é
    // link: fecha a gaveta e abre o `modals/modal-faq` de 2020 pelo mesmo
    // `CustomEvent('open')` que o rodapé do design despacha (ADR 0047). Se o
    // modal não estiver na página, o clique só fecha a gaveta.
    nav.addEventListener('click', (event) => {
        if (event.target.closest('a')) setOpen(false);

        if (event.target.closest('[data-open-modal-faq]')) {
            setOpen(false);
            document.querySelector('[data-component-name="modals/modal-faq"]')
                ?.dispatchEvent(new CustomEvent('open'));
        }
    });

    // Voltando para desktop, o estado do menu não pode ficar preso. O degrau é
    // o `Asa.desktop` (1025px = `@md + 1` de _variables.less).
    Asa.desktop.addEventListener('change', (event) => {
        if (event.matches) setOpen(false);
    });

    /* ── O PAINEL DE REGIÃO E IDIOMA DA GAVETA (10773:96119/96179, ADR 0036) ──
       Abaixo do degrau o globo abre a gaveta neste modo (`header--menu-idiomas`)
       — e o link "Altere sua região e idioma" da própria gaveta também. Dentro:
       o seletor abre e fecha a lista de rádios; mudar a seleção habilita o
       "Salvar alterações"; salvar aplica a região nas duas listas e na barra,
       fecha a gaveta e navega se houver destino. */
    const painel = component.querySelector('.header__idiomas-painel');
    const seletor = component.querySelector('.header__regiao-seletor');
    const regioes = component.querySelector('.header__regioes');
    const salvar = component.querySelector('.header__idiomas-salvar');

    if (painel && seletor && regioes && salvar) {
        const MODO = 'header--menu-idiomas';
        const LISTA_ABERTA = 'header__regioes--aberta';
        const radios = Array.from(regioes.querySelectorAll('input[type="radio"]'));
        const codigo = component.querySelector('.header__locale-codigo');
        const barraBandeira = component.querySelector('.header__locale-bandeira');
        const seletorBandeira = seletor.querySelector('.header__regiao-seletor-bandeira');
        const seletorTexto = seletor.querySelector('.header__regiao-seletor-texto');

        /** A região em que a página está — a que o "Salvar" compara. */
        let atual = (radios.find((r) => r.checked) || radios[0]).value;

        const marcado = () => radios.find((r) => r.checked) || null;

        const listaAberta = () => seletor.getAttribute('aria-expanded') === 'true';

        const abrirLista = () => {
            if (listaAberta()) return;
            seletor.setAttribute('aria-expanded', 'true');
            regioes.hidden = false;
            // A classe entra no quadro seguinte ao `display`, para a entrada
            // (opacidade e 8 de subida) chegar a transicionar.
            requestAnimationFrame(() => regioes.classList.add(LISTA_ABERTA));
        };

        const fecharLista = () => {
            if (!listaAberta()) return;
            seletor.setAttribute('aria-expanded', 'false');
            regioes.classList.remove(LISTA_ABERTA);
            regioes.hidden = true;
        };

        /** O seletor mostra a opção marcada; o botão só ativa se ela mudou. */
        const refletir = () => {
            const r = marcado();

            if (!r) return;

            if (seletorTexto) seletorTexto.textContent = r.dataset.rotulo || '';
            // A URL vem do Blade (`asset('assets/site/icons/…')`); aqui só troca
            // o nome do arquivo — um caminho montado à mão saía relativo e sem o
            // `site/`, e a bandeira quebrava (16/09).
            if (seletorBandeira) seletorBandeira.src = seletorBandeira.src.replace(/[^/]+\.svg$/, `${r.dataset.bandeira}.svg`);
            salvar.disabled = r.value === atual;
        };

        const abrirIdiomasGaveta = () => {
            setOpen(true);
            component.classList.add(MODO);
            fecharLista();
            refletir();

            // O painel entra dissolvendo (ver `header--idiomas-in` em
            // _header.less), no quadro seguinte ao `display`.
            if (Asa.movimentoPermitido()) {
                void painel.offsetWidth;
                requestAnimationFrame(() => component.classList.add('header--idiomas-in'));
            } else {
                component.classList.add('header--idiomas-in');
            }

            seletor.focus({ preventScroll: true });
        };

        seletor.addEventListener('click', () => {
            if (listaAberta()) {
                fecharLista();
            } else {
                abrirLista();
            }
        });

        regioes.addEventListener('change', refletir);

        // Esc na lista aberta fecha só a lista (o Esc do documento, acima, fecha
        // a gaveta inteira — daí parar a propagação aqui).
        painel.addEventListener('keydown', (event) => {
            if (event.key === 'Escape' && listaAberta()) {
                event.stopPropagation();
                fecharLista();
                seletor.focus();
            }
        });

        painel.addEventListener('submit', (event) => {
            event.preventDefault();

            const r = marcado();

            if (!r || r.value === atual) return;

            atual = r.value;
            Asa.track('language_change', { to_lang: r.value, method: 'panel' });

            // A região passa a valer nas duas listas e na barra.
            component.querySelectorAll('.header__idioma').forEach((a) => {
                if (a.getAttribute('data-valor') === atual) {
                    a.setAttribute('aria-current', 'true');
                } else {
                    a.removeAttribute('aria-current');
                }
            });

            if (codigo) codigo.textContent = r.dataset.codigo || codigo.textContent;
            if (barraBandeira) barraBandeira.src = barraBandeira.src.replace(/[^/]+\.svg$/, `${r.dataset.bandeira}.svg`);
            locale.setAttribute('aria-label', `Região e idioma: ${r.dataset.rotulo || ''}`);

            refletir();
            setOpen(false);

            // O destino é esta página no outro idioma (ou o site americano);
            // `#` é a rede para uma região que ainda não tenha destino.
            if (r.dataset.href && r.dataset.href !== '#') window.location.assign(r.dataset.href);
        });

        // O link da gaveta.
        nav.addEventListener('click', (event) => {
            const gatilho = event.target.closest('[data-abrir-idiomas]');

            if (!gatilho) return;

            event.preventDefault();
            // O clique num link da gaveta a fecha (acima); reabre já no modo.
            abrirIdiomasGaveta();
        });

        // O globo da barra, abaixo do degrau (o desktop tem a lista pendurada —
        // ver o menu de idiomas, abaixo).
        component.querySelector('.header__locale')?.addEventListener('click', () => {
            if (Asa.desktop.matches) return;
            abrirIdiomasGaveta();
            Asa.track('menu_toggle', { target_id: 'region', state: 'open' });
        });
    }
}

/* O menu de idiomas. O estado visual é `header--idioma-aberto` no header (a
   ordem de pintura e o `translate: none` que o véu fixo exige moram lá, ver
   _header.less); a saída passa por `header--idioma-fechando` enquanto o véu
   esmaece, para o header só voltar ao z-index de sempre depois. */
const locale = component.querySelector('.header__locale');
const idiomas = component.querySelector('.header__idiomas');
const idiomaVeu = component.querySelector('.header__idioma-veu');

if (locale && idiomas && idiomaVeu) {
    const IDIOMA_ABERTO = 'header--idioma-aberto';
    const IDIOMA_FECHANDO = 'header--idioma-fechando';
    // A transição mais longa da saída é 0,2s; a folga cobre o quadro seguinte.
    const IDIOMA_SAIDA = 260;

    const itens = Array.from(idiomas.querySelectorAll('a'));
    let timerSaida = 0;

    const idiomaAberto = () => locale.getAttribute('aria-expanded') === 'true';

    const abrirIdiomas = () => {
        if (idiomaAberto()) return;

        clearTimeout(timerSaida);
        component.classList.remove(IDIOMA_FECHANDO);
        component.classList.add(IDIOMA_ABERTO);
        locale.setAttribute('aria-expanded', 'true');
        Asa.travarRolagem('idioma');

        // O foco vai para a região atual (ou a primeira). A classe acima já
        // tornou a lista visível — `focus()` força o recálculo de estilo.
        const atual = itens.find((a) => a.hasAttribute('aria-current')) || itens[0];

        if (atual) atual.focus({ preventScroll: true });
    };

    const fecharIdiomas = (devolverFoco = false) => {
        if (!idiomaAberto()) return;

        locale.setAttribute('aria-expanded', 'false');
        component.classList.remove(IDIOMA_ABERTO);
        component.classList.add(IDIOMA_FECHANDO);
        Asa.soltarRolagem('idioma');

        clearTimeout(timerSaida);
        timerSaida = setTimeout(() => {
            component.classList.remove(IDIOMA_FECHANDO);
        }, Asa.movimentoPermitido() ? IDIOMA_SAIDA : 0);

        // `preventScroll`: o botão está à vista por definição (o menu pende
        // dele), e sem isso o Chrome rolava a página ~450px para cima ao
        // devolver o foco com o header preso na volta (ADR 0082).
        if (devolverFoco) locale.focus({ preventScroll: true });
    };

    locale.addEventListener('click', () => {
        // Abaixo do degrau o globo abre a gaveta no modo de idiomas (acima).
        if (!Asa.desktop.matches) return;

        if (idiomaAberto()) {
            fecharIdiomas();
            Asa.track('menu_toggle', { target_id: 'language', state: 'close' });
        } else {
            abrirIdiomas();
            Asa.track('menu_toggle', { target_id: 'language', state: 'open' });
        }
    });

    // Cruzando o degrau para baixo com a lista aberta, ela fecha: lá quem vale
    // é a gaveta.
    Asa.desktop.addEventListener('change', (event) => {
        if (!event.matches) fecharIdiomas();
    });

    // O véu cobre a tela inteira: clicar fora É clicar nele.
    idiomaVeu.addEventListener('click', () => fecharIdiomas(true));

    // Escolher uma região fecha (os destinos ainda são `#`).
    idiomas.addEventListener('click', (event) => {
        if (event.target.closest('a')) fecharIdiomas(true);
    });

    // Teclado: Esc fecha e devolve o foco; setas andam pela lista em laço;
    // Home/End vão às pontas. Do botão, seta para baixo entra na lista.
    component.addEventListener('keydown', (event) => {
        if (!idiomaAberto()) return;

        if (event.key === 'Escape') {
            event.preventDefault();
            fecharIdiomas(true);
            return;
        }

        const ativo = document.activeElement;
        const i = itens.indexOf(/** @type {HTMLAnchorElement} */ (ativo));

        if (i === -1 && ativo !== locale) return;

        const ultimo = itens.length - 1;
        let alvo = null;

        if (event.key === 'ArrowDown') {
            alvo = itens[i === -1 || i === ultimo ? 0 : i + 1];
        } else if (event.key === 'ArrowUp') {
            alvo = itens[i <= 0 ? ultimo : i - 1];
        } else if (event.key === 'Home') {
            alvo = itens[0];
        } else if (event.key === 'End') {
            alvo = itens[ultimo];
        }

        if (alvo) {
            event.preventDefault();
            alvo.focus();
        }
    });

    // Tab para fora da lista fecha sem roubar o foco. `relatedTarget` nulo é
    // clique em algo não focável (o véu, que já fecha) ou a janela perdendo o
    // foco — nos dois casos não há para onde devolver.
    component.addEventListener('focusout', (event) => {
        if (!idiomaAberto()) return;

        const destino = /** @type {Element|null} */ (event.relatedTarget);

        if (!destino) return;
        if (destino === locale || idiomas.contains(destino)) return;

        fecharIdiomas();
    });
}

// Faz a linha do item ativo nascer do centro no carregamento.
Asa.revelar(component);

});
